import * as React from 'react';
import styled from 'styled-components';
import {observer} from 'mobx-react';
import {CourseStatus, getStudentList, StudentItemObject} from 'api/course';
import Button, {ButtonVariant} from 'components/Button';
import useLoading from 'hooks/use-loading';
import Header from 'components/Header';
import HeaderSearch from 'components/HeaderSearch';
import Table from 'components/Table';
import {DropdownAlign, DropdownSide} from 'components/Dropdown';
import {StudentAfterAccess} from 'components/StudentAfterAccess';
import UserStore from 'stores/UserStore';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import useModal from 'hooks/use-modal';
import Container from 'containers/Container';
import Switch from 'components/Switch';
import {InviteModal} from 'components/InviteModal';
import {Filter} from '../../components/Filter';
import Radio from '../../components/Radio';
import {Helmet} from 'react-helmet';
import {UseTableHooks} from 'react-table';
import {StudentObject} from '../../api/student/types';
import TabStatistics from "./TabStatistics";

const Controls = styled.div`
  align-content: center;
  justify-content: flex-end;
  display: flex;
`;

export const Filters = styled.div`
  margin-top: 20px;
  justify-content: space-between;
  display: flex;
`;

/**
 * The students page
 * @constructor
 */
function Students(): React.ReactElement {
  const {fetch, data, render} = useLoading<StudentItemObject[]>();
  const inviteModal = useModal();

  React.useEffect(() => {
    fetch(() => new Promise((resolve) => (
      getStudentList(0, 999).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          resolve(response);
        }
      })
    )));
  }, []);

  /**
   * This function renders the page header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    return (
      <Header>
        <HeaderSearch
          inputSearch={{
            placeholder: 'Поиск по имени, телефону, e-mail и курсу',
          }}
          renderAfter={UserStore.isTeacher() ? (
            <React.Fragment>
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => (
                  inviteModal.setModalVisible(
                    !inviteModal.isModalVisible
                  )
                )}
              >
                Добавить студента
              </Button>
              {inviteModal.isModalVisible && (
                <InviteModal
                  onBlur={() => (
                    inviteModal.setModalVisible(false)
                  )}
                />
              )}
            </React.Fragment>
          ) : undefined}
        />
        <Filters>
          <Filter controls>
            <Filter.Group name="status" label="Публикация">
              {(group) => (
                [
                  (
                    <Filter.Field
                      type="radio"
                      label="Опубликовано"
                      value={CourseStatus.Public}>
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Опубликовано"
                        />
                      )}
                    </Filter.Field>
                  ),
                  (
                    <Filter.Field
                      type="radio"
                      label="Не опубликовано"
                      value={CourseStatus.Draft}>
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Не опубликовано"
                        />
                      )}
                    </Filter.Field>
                  ),
                ]
              )}
            </Filter.Group>
            <Filter.Group name="createdAt" label="Дата создания">
              {(group) => (
                [
                  (
                    <Filter.Field
                      type="radio"
                      label="Сегодня"
                      value="today">
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Сегодня"
                        />
                      )}
                    </Filter.Field>
                  ),
                  (
                    <Filter.Field
                      type="radio"
                      label="Вчера"
                      value="tomorrow">
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Вчера"
                        />
                      )}
                    </Filter.Field>
                  ),
                ]
              )}
            </Filter.Group>
          </Filter>
        </Filters>
      </Header>
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    console.log(data);
    return (
      <Table
        columns={[
          {
            Header: '',
            accessor: 'toggle',
            Cell: () => (
              <Switch />
            ),
          },
          {
            Header: 'Имя студента',
            accessor: 'name',
            Cell: (props: UseTableHooks<Pick<StudentObject, 'name'>>) => {
              console.log(props);
              return (
                <a href={`/students/${props.cell.value.id}`} target="_blank" rel="noreferrer">
                  {props.cell.value.name}
                </a>
              );
            },
          },
          {
            Header: 'Email',
            accessor: 'email',
          },
          {
            Header: 'Курс',
            accessor: 'courseTitle',
          },
          {
            Header: '',
            accessor: 'status',
            Cell: () => (
              <Controls>
                <StudentAfterAccess
                  side={DropdownSide.Bottom}
                  align={DropdownAlign.Right}
                />
              </Controls>
            ),
          },
        ]}
        data={data?.map((student) => ({
          toggle: student.status,
          name: {
            name: student.name,
            id: student.userID,
          },
          email: student.email,
          courseTitle: student.courseTitle,
          status: student.status,
        })) || []}
      />
    );
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <div>
        Студенты не найдены
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>GrowPlace / Студенты</title>
      </Helmet>
      {renderHeader()}
      <Container>
        {/*{render(renderContent, renderNotFound)}*/}
        <TabStatistics />
      </Container>
    </React.Fragment>
  );
}

export default observer(Students);
